import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Modal } from 'flowbite';
import moment from 'moment';
import {
  IAffectedReservation,
  IAffectedReservationVariation,
  IReservationRestrictionWarning,
} from 'src/app/models/booking/restrictionWarning';
import { ReservationFailureReason } from 'src/app/models/enums/reservationFailureReason';
import { LiteralService, UtilsService } from 'src/app/services';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';

const AFFECTED_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';

// Confirming names exactly the restrictions shown, never a blanket yes, so a later one is asked in its turn
@Component({
  selector: 'app-modal-restriction-warnings',
  templateUrl: './modal-restriction-warnings.component.html',
  styleUrl: './modal-restriction-warnings.component.scss',
  standalone: false,
})
export class ModalRestrictionWarningsComponent implements OnChanges {
  @Input() id: string;
  @Input() modal: Modal;
  @Input() warnings: IReservationRestrictionWarning[] = [];
  @Output() confirm = new EventEmitter<ReservationFailureReason[]>();
  @Output() close = new EventEmitter<void>();

  // Grouped once per warnings: regrouped on each check, ngFor recreates the rows and their links lose every hover and click
  public affectedByWarning: IAffectedReservation[][] = [];
  public images = images;
  public TooltipPosition = TooltipPosition;

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['warnings']) return;
    this.affectedByWarning = (this.warnings ?? []).map((warning) => this.affectedReservations(warning));
  }

  public reasonLabel(reason: ReservationFailureReason): string {
    return this.literalService.get(`bookings.restrictions.reasons.${reason}`, false);
  }

  // A passenger can be pushed at both stops, and is listed once with a stop line for each
  public affectedReservations(warning: IReservationRestrictionWarning): IAffectedReservation[] {
    const byReservation = new Map<number, IAffectedReservation>();
    (warning.affectedReservations ?? []).forEach((affected) => {
      const listed = byReservation.get(affected.reservationId);
      if (listed) {
        listed.stops.push(affected);
        return;
      }
      byReservation.set(affected.reservationId, {
        reservationId: affected.reservationId,
        userName: affected.userName,
        stops: [affected],
      });
    });
    return [...byReservation.values()];
  }

  public bookingUrl(reservationId: number): string {
    return `/bookings/${reservationId}`;
  }

  public timeOf(dateTime?: string): string {
    return dateTime ? moment(dateTime, AFFECTED_DATE_TIME_FORMAT).format('HH:mm') : '';
  }

  // The whole shift from the time the passenger was told, not just what exceeds the allowance
  public minutesDifference(affected: IAffectedReservationVariation): number | null {
    if (!affected.communicatedDateTime || !affected.proposedDateTime) return null;
    return moment(affected.proposedDateTime, AFFECTED_DATE_TIME_FORMAT)
      .diff(moment(affected.communicatedDateTime, AFFECTED_DATE_TIME_FORMAT), 'minutes');
  }

  public signedMinutes(affected: IAffectedReservationVariation): string {
    const minutes = this.minutesDifference(affected);
    if (minutes === null) return '';
    return `${minutes > 0 ? '+' : ''}${minutes}`;
  }

  public onConfirm(): void {
    this.confirm.emit(this.warnings.map((warning) => warning.reason));
    this.modal.hide();
  }

  public onClose(): void {
    this.modal.hide();
    this.close.emit();
  }
}
